import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "./SmoothScroll";
import { Medallion } from "./Medallion";

const LINES = [
  { text: "Find the", cls: "text-white" },
  { text: "suppliers", cls: "text-gold-purple italic" },
  { text: "that flip.", cls: "text-white" },
];
const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden pt-16" data-testid="hero-section">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src="/img/hero.webp" alt="" className="w-full h-full object-cover opacity-40" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#09040E]/60 via-[#09040E]/70 to-[#09040E]" />
      <div className="absolute -left-40 top-1/3 w-[38rem] h-[38rem] rounded-full bg-violet-600/25 blur-[140px]" />
      <div className="absolute right-0 bottom-0 w-[30rem] h-[30rem] rounded-full bg-amber-500/10 blur-[140px]" />
      <div className="grain absolute inset-0 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-14 sm:pt-20 pb-20 grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center min-h-[calc(100svh-4rem)]">
        <div>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
            className="text-[11px] sm:text-xs tracking-[0.4em] uppercase text-gold mb-8 flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="h-px w-10 bg-gold/70" /> Luxury resale · Supplier vault
          </motion.p>
          <h1 className="font-display font-black uppercase leading-[0.92] text-[3.1rem] sm:text-7xl lg:text-[6.6rem] tracking-tight" data-testid="hero-headline">
            {LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-2">
                <motion.span className={`block ${l.cls}`} initial={{ y: "105%" }} animate={{ y: "0%" }}
                  transition={{ duration: 1.1, ease, delay: 0.45 + i * 0.14 }}>
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}
            className="mt-8 max-w-md text-base sm:text-lg text-violet-100/85 leading-relaxed italic font-display" data-testid="hero-tagline">
            Helping resellers find the suppliers they need to grow, one connection at a time.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap items-center gap-5">
            <button onClick={() => scrollToId("suppliers")} className="btn-gold px-8 py-4 text-xs group" data-testid="hero-shop-suppliers-btn">
              Shop suppliers <ArrowDown className="w-4 h-4 ml-2 transition-transform group-hover:translate-y-0.5" />
            </button>
            <span className="text-xs tracking-[0.25em] uppercase text-violet-300">8 vaults · instant access</span>
          </motion.div>
        </div>
        <Medallion />
      </div>
    </section>
  );
};
